import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { ShieldWarning, Broadcast } from "@phosphor-icons/react";

export default function LiveAppealCard({ user }) {
    const [appeals, setAppeals] = useState([]);
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = () => api.get("/live-appeals/me").then(({ data }) => setAppeals(data)).catch(() => {});
    useEffect(() => { load(); }, []);

    if (!user?.live_disabled) return null;

    const pending = appeals.find((a) => a.status === "pending");

    const submit = async () => {
        if (!message.trim() || message.trim().length < 20) {
            return toast.error("Tell us at least 20 characters about why you should be reinstated");
        }
        setSubmitting(true);
        try {
            await api.post("/live-appeals", { message: message.trim() });
            toast.success("Appeal submitted — admin will review it");
            setMessage("");
            await load();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="live-appeal-card" className="bg-red-500/5 border border-red-500/40 rounded-sm p-5 mt-6">
            <div className="flex items-center gap-2 mb-2">
                <ShieldWarning size={20} weight="fill" className="text-red-400" />
                <div className="font-heading text-lg tracking-wider uppercase text-red-300">Live access suspended</div>
            </div>
            <div className="text-sm text-wta-muted mb-4">
                {user.live_disabled_reason || "Your live streaming was suspended after content-policy warnings."}
            </div>

            {pending ? (
                <div data-testid="appeal-pending" className="bg-wta-surface2 border border-wta-border rounded-sm p-3 text-sm">
                    <div className="text-xs tracking-widest text-wta-muted uppercase">Appeal pending</div>
                    <div className="mt-1 text-white/90">{pending.message}</div>
                    <div className="text-[10px] text-wta-muted mt-1">Submitted {new Date(pending.created_at).toLocaleString()}</div>
                </div>
            ) : (
                <div className="space-y-2">
                    <textarea
                        data-testid="appeal-message"
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={1000}
                        placeholder="Why should we restore your live streaming privileges?"
                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                    />
                    <div className="flex items-center justify-between">
                        <div className="text-[10px] text-wta-muted">{message.length}/1000</div>
                        <button
                            data-testid="appeal-submit"
                            onClick={submit}
                            disabled={submitting}
                            className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-50"
                        >
                            <Broadcast size={14} weight="fill" /> {submitting ? "Submitting…" : "Submit Appeal"}
                        </button>
                    </div>
                </div>
            )}

            {appeals.filter((a) => a.status !== "pending").length > 0 && (
                <div className="mt-4 space-y-2">
                    <div className="text-xs tracking-widest text-wta-muted uppercase">Past appeals</div>
                    {appeals.filter((a) => a.status !== "pending").slice(0, 3).map((a) => (
                        <div key={a.id} className="bg-wta-surface2 border border-wta-border rounded-sm p-2 text-xs flex items-center justify-between">
                            <span className="truncate">{a.message}</span>
                            <span className={`ml-2 shrink-0 font-heading uppercase tracking-widest ${a.status === "approved" ? "text-wta-primary" : "text-red-300"}`}>{a.status}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
